/**
 * 外链文章跳转规则（2026-09-18）
 *
 * 背景：内容可以配置一个「外链地址」（contents.link_url）。填了用户端点击该条目
 * 就直接跳转，不进入本地详情页。
 *
 * 跳转规则
 * ─────────
 * 1. 公众号文章（https://mp.weixin.qq.com/...）
 *    → 直接跳转。微信内（含小程序 web-view）可以正常打开公众号文章，
 *      前提是该小程序已在公众号后台完成关联；未关联时会由微信自己给出提示。
 *
 * 2. 其他外链
 *    → 微信内：**不能**直接跳。小程序 web-view 只能打开「已配置业务域名」的网页，
 *      而业务域名要能往对方域名根目录放校验文件才配得成 —— 别人的站点一律配不了，
 *      硬跳会白屏或报「不支持打开非业务域名」。所以改为弹窗引导用户复制链接，
 *      到系统浏览器打开。
 *    → 非微信环境（普通浏览器 / 手机浏览器）：本来就是浏览器，直接打开即可，
 *      再弹「请复制到浏览器打开」是反直觉的，因此直接放行。
 *
 * ⚠️ 别把这条规则简化为「一律弹复制」：H5 也跑在普通浏览器里，
 *    那样会让本来就正常的浏览器场景变得很别扭。
 */

/** 微信内可直接打开的域名（公众号文章） */
const WECHAT_ARTICLE_HOSTS = ['mp.weixin.qq.com']

/** 是否处于微信环境（微信内置浏览器或小程序 web-view） */
export function isWechatEnv(): boolean {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent)
}

/** 是否运行在小程序的 web-view 里（UA 带 miniProgram 标识） */
export function isMiniProgramWebview(): boolean {
  if (typeof navigator === 'undefined') return false
  return /miniProgram/i.test(navigator.userAgent)
}

/**
 * 是否是「微信内可直接打开」的公众号文章链接。
 * 用 URL 解析而不是 startsWith：能自然处理 http/https、大小写、带空格等情况。
 */
export function isWechatArticleUrl(url: string): boolean {
  const raw = (url || '').trim()
  if (!raw) return false
  try {
    const u = new URL(raw)
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false
    return WECHAT_ARTICLE_HOSTS.includes(u.hostname.toLowerCase())
  } catch {
    return false
  }
}

/** 外链是否是可用的 http(s) 地址 */
export function isHttpUrl(url: string): boolean {
  const raw = (url || '').trim()
  if (!raw) return false
  try {
    const u = new URL(raw)
    return u.protocol === 'https:' || u.protocol === 'http:'
  } catch {
    return false
  }
}

export type ExternalOpenAction =
  /** 已直接跳转 */
  | { kind: 'navigated' }
  /** 需要弹窗引导用户复制链接到浏览器打开 */
  | { kind: 'copy'; url: string; reason: 'wechat-non-article' | 'invalid' }

/**
 * 解析并执行外链打开。返回实际采取的动作，便于调用方决定是否弹提示。
 *
 * @param url       外链地址
 * @param opts.openInNewTab 非微信环境下是否新开标签页（默认 false，当前窗口打开）
 */
export function openExternalArticle(
  url: string,
  opts: { openInNewTab?: boolean } = {},
): ExternalOpenAction {
  const target = (url || '').trim()

  if (!isHttpUrl(target)) {
    return { kind: 'copy', url: target, reason: 'invalid' }
  }

  // 公众号文章：微信内外都能开
  if (isWechatArticleUrl(target)) {
    window.location.href = target
    return { kind: 'navigated' }
  }

  // 非公众号链接：只有不在微信里时才可以直接打开
  if (!isWechatEnv()) {
    if (opts.openInNewTab) window.open(target, '_blank', 'noopener,noreferrer')
    else window.location.href = target
    return { kind: 'navigated' }
  }

  return { kind: 'copy', url: target, reason: 'wechat-non-article' }
}

/** 复制文本到剪贴板；navigator.clipboard 不可用时回退到 execCommand */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* 落到下面的兜底 */
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
